import { DecimalPipe } from '@angular/common';
import { GB_PER_TB } from '../../../core/services/quick-sizer-response.mapper';
import { DomainComponentPart, QuickSizerOutput } from '../models/quick-sizer.models';

/** The VCF components row in the memory, CPU and storage charts, on screen and in the PDF. */
export const VCF_COMPONENTS_CHARGED_LABEL = 'VCF components (expected use)';

/** The engine's VCF component row in any breakdown: memory, cores or storage. */
const VCF_COMPONENT_ROW = /^vcf component/i;

export function isVcfComponentRow(label: string): boolean {
  return VCF_COMPONENT_ROW.test(label);
}

/** One explanation per chart, from the Management Domain table totals to the charted figure. */
export interface ComponentChargeNotes {
  memory: string;
  cpu: string;
  storage: string;
}

/**
 * How each chart's VCF components figure follows from the component totals, ending on the engine's
 * figure. Shared with the PDF export.
 */
export function componentChargeNotes(
  totals: Pick<DomainComponentPart, 'vcpu' | 'memoryGB' | 'storageGB'>,
  output: QuickSizerOutput,
  vcpuToCoreRatio: number,
  decimal: DecimalPipe,
): ComponentChargeNotes {
  const { avgCpuUtilizationPercent, avgMemoryUtilizationPercent, growthPercent } = output.workloadDemand;
  const num = (value: number, digits: string) => decimal.transform(value, digits) ?? '0';
  const gb = (value: number) => `${num(value, '1.0-1')} GB`;
  const tb = (valueGb: number) => `${num(valueGb / GB_PER_TB, '1.2-2')} TB`;
  const share = (percent: number) => num(percent / 100, '1.0-2');
  const growth = num(1 + growthPercent / 100, '1.0-2');
  const growthText = `${num(growthPercent, '1.0-1')}% growth`;

  const memoryGb = output.memoryBreakdown.find(row => isVcfComponentRow(row.label))?.valueGB ?? 0;
  const cores = output.cpuBreakdown.find(row => isVcfComponentRow(row.label))?.valueCores ?? 0;
  const storageGb = output.capacityWaterfall.overheads.find(row => isVcfComponentRow(row.label))?.valueGb ?? 0;
  const vcpuCores = vcpuToCoreRatio > 0 ? totals.vcpu / vcpuToCoreRatio : totals.vcpu;
  const coresText = (value: number) => {
    const text = num(value, '1.0-1');
    return `${text} ${text === '1' ? 'core' : 'cores'}`;
  };

  return {
    memory: `The management domain components total ${gb(totals.memoryGB)} of RAM. They are charged like `
      + `your VMs, at ${num(avgMemoryUtilizationPercent, '1.0-1')}% average use plus ${growthText}: `
      + `${gb(totals.memoryGB)} × ${share(avgMemoryUtilizationPercent)} × ${growth} = ${gb(memoryGb)}.`,
    cpu: `The management domain components total ${num(totals.vcpu, '1.0-0')} vCPU, which is `
      + `${coresText(vcpuCores)} at ${num(vcpuToCoreRatio, '1.0-2')} vCPU / pCore. They are charged `
      + `like your VMs, at ${num(avgCpuUtilizationPercent, '1.0-1')}% average use plus ${growthText}: `
      + `${num(vcpuCores, '1.0-1')} × ${share(avgCpuUtilizationPercent)} × ${growth} = ${coresText(cores)}.`,
    storage: `The management domain components total ${tb(totals.storageGB)} of storage. They are charged `
      + `like your VMs, with ${growthText}: ${tb(totals.storageGB)} × ${growth} = ${tb(storageGb)}.`,
  };
}
